import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor } from "@/components/ui/popover";
import useFilterInput from "@/hooks/useFilterInput";
import { cn } from "@/lib/utils";
import React, { forwardRef, useState } from "react";
import { InputProps } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { SuggestionList } from "./SuggestionList";

export type FilterInputWithSuggestionsProps = {
    values: string[];
    setValues: (values: string[]) => void;
    className?: string;
    validator?: (value: string) => boolean;
    containerClassName?: string;
    suggestionListClassName?: string;
    id?: string;
    getUnavailableReason?: (value: string) => string | undefined;
} & Omit<InputProps, "ref">;

const FilterInputWithSuggestions = forwardRef<
    HTMLInputElement,
    FilterInputWithSuggestionsProps
>(
    (
        {
            values,
            setValues,
            className,
            validator,
            containerClassName,
            suggestionListClassName,
            id,
            getUnavailableReason,
            ...props
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const [isOpen, setIsOpen] = useState(false);

        const {
            newKeyword,
            wrongInput,
            suggestions,
            handleChange,
            handleKeyDown,
            handleBlur,
            handleSuggestionSelect,
        } = useFilterInput(values, setValues, validator, getUnavailableReason);

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(e);
            if (e.target.value) {
                setIsOpen(true);
            }
        };

        const handleSuggestionClick = (suggestion: string) => {
            handleSuggestionSelect(suggestion);
            setIsOpen(false);
        };

        return (
            <Popover
                onOpenChange={setIsOpen}
                open={isOpen && suggestions.length > 0}
            >
                <PopoverAnchor className={cn("relative", containerClassName)}>
                    <Input
                        ref={ref}
                        className={cn(
                            wrongInput && "text-destructive",
                            className,
                        )}
                        id={id}
                        onBlur={handleBlur}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={t("customViewDialog.viewConfig.writeHere")}
                        value={newKeyword}
                        variant="ghost"
                        {...props}
                    />
                </PopoverAnchor>
                <SuggestionList
                    className={suggestionListClassName}
                    getUnavailableReason={getUnavailableReason}
                    onSelect={handleSuggestionClick}
                    suggestions={suggestions}
                />
            </Popover>
        );
    },
);

export default FilterInputWithSuggestions;
